import {
  applyAction,
  opponent,
  type Action,
  type MatchState,
  type MatchView,
  type Pcg32,
  type Side,
} from '../engine';
import { determinize } from './determinize';
import { BALANCED, GREEDY_SETTINGS } from './personality';
import { searchPlans, settle } from './planner';
import type { EvalContext } from './valuation';

/**
 * Round strategy (spec §6.2, §20): when to pass. Cards are the match-long resource, so a round is
 * worth only so many cards; passing while ahead forces the opponent to spend cards to overtake.
 */

export interface RoundSituation {
  readonly d: number;
  /** The lead leaves out the Long Night's head start (docs/SHOP_DESIGN.md §5.5). */
  readonly anteBlind: boolean;
  readonly oppPassed: boolean;
  readonly lastRound: boolean;
  /** Losing this round loses the match. */
  readonly critical: boolean;
  /** Winning this round wins the match. */
  readonly decisive: boolean;
  readonly myCards: number;
  readonly oppCards: number;
  /** Cards each side has played to the pile this round. */
  readonly mySpent: number;
  readonly oppSpent: number;
  /** Cards this side is prepared to spend on this round. */
  readonly budget: number;
  /** Value of winning this round, in cards. */
  readonly roundValue: number;
}

export function roundSituation(
  v: MatchView,
  me: Side,
  roundShare = BALANCED.roundShare,
): RoundSituation {
  const c = v.config.constants;
  const opp = opponent(me);
  const mine = v.players[me];
  const theirs = v.players[opp];
  const lastRound = v.round >= c.MAX_ROUNDS;
  const critical = theirs.roundWins >= c.ROUND_WINS_NEEDED - 1;
  const decisive = mine.roundWins >= c.ROUND_WINS_NEEDED - 1;
  const myCards = (mine.hand?.length ?? mine.handCount) + mine.deckCount;
  const oppCards = theirs.handCount + theirs.deckCount;
  let mySpent = 0;
  let oppSpent = 0;
  for (const e of v.pile) {
    if (e.source !== 'card') continue;
    if (e.playedBy === me) mySpent++;
    else oppSpent++;
  }
  const roundsLeft = c.MAX_ROUNDS - v.round;
  let budget: number;
  let roundValue: number;
  if (lastRound || critical) {
    budget = Infinity;
    roundValue = 20;
  } else if (decisive) {
    // One more win ends the match; losing this round still leaves a deciding round.
    budget = Math.ceil((myCards + mySpent) * 0.5);
    roundValue = 7;
  } else {
    // An open round with more rounds to come: invest a share of the remaining cards.
    const share = roundsLeft >= 2 ? roundShare : Math.min(0.75, roundShare + 0.1);
    budget = Math.ceil((myCards + mySpent) * share);
    roundValue = 5;
  }
  // Long Night (anteBlind): while the other side plays on, the head start does not count as a
  // lead, so the Broker's patrons and regulars contest the board instead of passing at once.
  const ante =
    v.config.ledger?.anteBlind && me === 'N' && !theirs.passed ? v.config.northHeadStart : 0;
  return {
    d: mine.roundScore - theirs.roundScore - ante,
    anteBlind: ante !== 0,
    oppPassed: theirs.passed,
    lastRound,
    critical,
    decisive,
    myCards,
    oppCards,
    mySpent,
    oppSpent,
    budget,
    roundValue,
  };
}

const GREEDY = GREEDY_SETTINGS;

const cardsHeld = (s: MatchState, side: Side): number =>
  s.players[side].hand.length + s.players[side].deck.length;

export interface ChaseResult {
  readonly overtook: boolean;
  /** Cards the chaser consumed. */
  readonly cards: number;
  readonly nodes: number;
}

/**
 * Plays `chaser` greedily on a determinized state in which the other side has passed, until it
 * leads at a turn start (it would then pass), runs out of turns, or the round ends.
 */
export function simulateChase(
  start: MatchState,
  chaser: Side,
  ctx: EvalContext,
  maxTurns = 8,
): ChaseResult {
  const greedyCtx: EvalContext = {
    ...ctx,
    me: chaser,
    opp: opponent(chaser),
    personality: BALANCED,
    settings: GREEDY,
    cardValue: 1,
  };
  let s = start;
  const round = s.round;
  const startCards = cardsHeld(s, chaser);
  let nodes = 0;
  let turns = 0;
  for (let guard = 0; guard < 80; guard++) {
    if (s.phase === 'MATCH_OVER' || s.round !== round) {
      const last = s.rounds[s.rounds.length - 1];
      return { overtook: last?.winner === chaser, cards: startCards - cardsHeld(s, chaser), nodes };
    }
    if (s.phase === 'CHOOSE_INSCRIPTION' || s.phase === 'CHOOSE_EPITAPH') {
      s = settle(s, s.decider, greedyCtx);
      continue;
    }
    if (s.decider !== chaser) break;
    if (s.phase === 'TURN_START') {
      const lead = s.players[chaser].roundScore - s.players[opponent(chaser)].roundScore;
      if (lead > 0 || turns >= maxTurns)
        return { overtook: lead > 0, cards: startCards - cardsHeld(s, chaser), nodes };
      const r = applyAction(s, { type: 'CONTINUE', side: chaser });
      if (r.error) break;
      s = r.state;
      turns++;
      continue;
    }
    if (s.phase === 'ACTION' || s.phase === 'CHAIN') {
      const res = searchPlans(s, greedyCtx);
      nodes += res.nodes;
      const best = res.plans[0];
      if (!best) break;
      s = best.end;
      continue;
    }
    break;
  }
  const lead = s.players[chaser].roundScore - s.players[opponent(chaser)].roundScore;
  return { overtook: lead > 0, cards: startCards - cardsHeld(s, chaser), nodes };
}

export interface ChaseEstimate {
  /** Probability that the chaser overtakes. */
  readonly p: number;
  /** Average cards consumed when it does. */
  readonly cost: number;
  readonly nodes: number;
}

/** Monte Carlo estimate over determinizations of a chase starting after `first` is applied. */
export function estimateChase(
  v: MatchView,
  first: Action | null,
  chaser: Side,
  ctx: EvalContext,
  rng: Pcg32,
  samples: number,
): ChaseEstimate {
  let wins = 0;
  let cost = 0;
  let nodes = 0;
  let runs = 0;
  for (let i = 0; i < samples; i++) {
    let s = determinize(v, rng);
    if (first) {
      const r = applyAction(s, first);
      if (r.error) continue;
      s = r.state;
    }
    const res = simulateChase(s, chaser, ctx);
    nodes += res.nodes;
    runs++;
    if (res.overtook) {
      wins++;
      cost += res.cards;
    }
  }
  return { p: runs ? wins / runs : 0, cost: wins ? cost / wins : Infinity, nodes };
}

/** Best points the side can score this turn after CONTINUE (the draw is sampled). */
export function bestGainAfterContinue(
  v: MatchView,
  ctx: EvalContext,
  rng: Pcg32,
  samples: number,
): { gain: number; nodes: number } {
  const greedyCtx: EvalContext = { ...ctx, settings: GREEDY, cardValue: 1 };
  let total = 0;
  let nodes = 0;
  let runs = 0;
  for (let i = 0; i < samples; i++) {
    const s = determinize(v, rng);
    const r = applyAction(s, { type: 'CONTINUE', side: ctx.me });
    if (r.error) continue;
    runs++;
    if (r.state.phase !== 'ACTION' || r.state.decider !== ctx.me) continue;
    const res = searchPlans(r.state, greedyCtx);
    nodes += res.nodes;
    total += res.plans.reduce((m, p) => Math.max(m, p.gain), 0);
  }
  return { gain: runs ? total / runs : 0, nodes };
}

export interface PassDecision {
  readonly action: Action;
  readonly reason: string;
  readonly nodes: number;
}

/** PASS or CONTINUE at turn start (§6.1). */
export function passOrContinue(v: MatchView, ctx: EvalContext, rng: Pcg32): PassDecision {
  const me = ctx.me;
  const r = roundSituation(v, me, ctx.personality.roundShare);
  const pass = (reason: string, nodes = 0): PassDecision => ({
    action: { type: 'PASS', side: me },
    reason,
    nodes,
  });
  const cont = (reason: string, nodes = 0): PassDecision => ({
    action: { type: 'CONTINUE', side: me },
    reason,
    nodes,
  });
  const hasResources = r.myCards > 0 || v.players[me].epitaphs.length > 0;
  const samples = Math.max(1, ctx.settings.samples + 1);

  if (!ctx.settings.tactical) {
    // Simple policy (easy): keep playing unless the round is already won.
    if (r.oppPassed && r.d > 0) return pass('ahead-opponent-passed');
    if (!r.oppPassed && r.d >= 45 && !r.critical && !r.lastRound) return pass('big-lead');
    // At a Long Night table even a simple player keeps cards for the rounds to come: behind,
    // with its share of the deck spent on an open round, it lets the round go (issue #10: the
    // Magpie emptied her deck chasing round one, and sat out round two on her head start).
    if (v.config.ledger && !r.critical && !r.lastRound && r.d < 0 && r.mySpent >= r.budget)
      return pass('cut-losses');
    return hasResources ? cont('play-on') : pass('no-resources');
  }

  if (r.oppPassed) {
    if (r.d > 0) return pass('ahead-opponent-passed');
    if (!hasResources) return pass('no-resources');
    if (r.critical || r.lastRound) return cont('must-win-round');
    const est = estimateChase(v, null, me, ctx, rng, samples);
    // Chase when the expected value of winning the round beats the cards it costs.
    const worth =
      est.p * r.roundValue * ctx.personality.aggression - est.p * est.cost * ctx.personality.thrift;
    const affordable = est.cost <= Math.max(2, r.budget - r.mySpent + 2);
    return est.p >= 0.5 && worth > 0 && affordable
      ? cont('chase', est.nodes)
      : pass('not-worth-chasing', est.nodes);
  }

  if (!hasResources) return pass('no-resources');

  if (r.d > 0 && r.anteBlind) {
    // Ahead without the head start: play the round's share of cards, then rest on the Ante.
    if (r.mySpent >= r.budget) return pass('round-budget-spent');
    const g = bestGainAfterContinue(v, ctx, rng, samples);
    return g.gain <= 0 ? pass('nothing-to-gain', g.nodes) : cont('extend-lead', g.nodes);
  }

  if (r.d > 0) {
    const est = estimateChase(v, { type: 'PASS', side: me }, ctx.opp, ctx, rng, samples);
    if (r.critical || r.lastRound) {
      return est.p <= 0.1 ? pass('lead-is-safe', est.nodes) : cont('protect-lead', est.nodes);
    }
    const lead = ctx.personality.passLead;
    if (est.p <= lead * 0.7) return pass('opponent-unlikely-to-overtake', est.nodes);
    if (est.cost >= 2 + (1 - lead) * 4) return pass('overtaking-costs-opponent', est.nodes);
    if (r.mySpent >= r.budget) return pass('round-budget-spent', est.nodes);
    // Continuing commits us to an action; if nothing scores, it only burns a card.
    const g = bestGainAfterContinue(v, ctx, rng, samples);
    if (g.gain <= 0) return pass('nothing-to-gain', est.nodes + g.nodes);
    return cont('extend-lead', est.nodes + g.nodes);
  }

  if (!r.critical && !r.lastRound) {
    if (r.mySpent >= r.budget && r.oppSpent <= r.mySpent + 1) {
      // We have invested our share of cards without taking the lead: stop feeding this round.
      return pass('cut-losses');
    }
    const g = bestGainAfterContinue(v, ctx, rng, samples);
    if (g.gain <= 0 && r.d < 0) return pass('nothing-to-gain', g.nodes);
    // Our share is spent and the round is out of reach (the best play now would not make up
    // half the deficit): keep the cards for the rounds to come, however much the other side
    // spends (issue #10: a regular emptied her deck chasing round one, and had none for two).
    if (r.mySpent >= r.budget && r.d < 0 && g.gain * 2 < -r.d) return pass('out-of-reach', g.nodes);
    return cont('play-on', g.nodes);
  }
  return cont('play-on');
}
